import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import React, { useCallback, useEffect } from 'react';

import { Button, Dialog, DialogActions, DialogContent, IconButton } from '@mui/material';
import { format } from 'date-fns';
import numeral from 'numeral';
import { getTokenHistory } from '../api/auth';
import tableStyles from "../styles/components/table.module.scss";
import { ErrorMessage, WarningMessage } from './error-message';
import Loader from './loader';
import { ModalTitle } from './modal-title';


interface TokensLog {
  id: string | number
  created_at: string
  path: string
  time_to_execute: number
  message?: string | null
}

interface TokensHistoryModalProps {
  onClose: () => void
  tokenId: string | number | null
}

export const TokensHistoryModal = ({
  onClose,
  tokenId,
}: TokensHistoryModalProps) => {
  const [error, setError] = React.useState<string[]>([])
  const [loading, setLoading] = React.useState(false)
  const [logs, setLogs] = React.useState<TokensLog[] | null>(null)

  const fetchLogs = useCallback(() => {
    const execute = async () => {
      const result = await getTokenHistory({
        tokenId,
      })
      if (result) {
        const {response, error, message} = result
        if (error !== 200) {
          setError(e=> [...e, message] as string[])
        } else {
          setLogs(response?.tokens || [])
        }
      }
      setLoading(false)
    }
    execute()
  }, [tokenId])

  useEffect(() => {
    setError([])
    setLoading(true)
    if (tokenId) {
      fetchLogs()
    }
  }, [tokenId])


  const columns: any = [
    {
      field: 'created_at',
      headerName: 'Created at',
      width: 150,
      editable: false,
      valueGetter: (params: any) => {
        const date = new Date(params?.value);
        return format(new Date(date), 'HH:mm:ss dd/MM/yy')
      }
    },
    {
      field: 'path',
      headerName: 'End point path',
      width: 250,
      editable: false,
      valueGetter: (params: any) => {
        return `${params?.row?.method ? `[${params?.row.method}] ` : ``}${params?.value}`
      }
    },
    {
      field: 'time_to_execute',
      headerName: 'Execution (ms)',
      type: 'number',
      width: 120,
      editable: false,
      valueGetter: (params: any) => {
        return `${params?.value ? numeral(params?.value).format(params?.value > 0 ? "0,0" : "0.000") : ''}`
      },
    },
    {
      field: 'message',
      headerName: 'Message',
      type: 'string',
      width: 280,
      editable: false,
      valueGetter: (params: any) => `${params?.value || ''}`,
    },
  ];

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <ModalTitle onClose={onClose}>API token history log <IconButton style={{marginLeft: '16px'}} onClick={() => {
        setError([])
        setLoading(true)
        fetchLogs();
      }}><RefreshIcon /></IconButton> </ModalTitle>
      <DialogContent>
        {loading ?
          <Loader small center />
          :
          <>
            <ErrorMessage message={error} />
            {(logs && Array.isArray(logs) && logs.length > 0) ? <Box sx={{ height: 400, width: '100%' }}>
              <DataGrid
                rows={logs || []}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 25,
                    },
                  },
                }}
                pageSizeOptions={[200]}
                // checkboxSelection
                disableRowSelectionOnClick
                getRowClassName={(params) => {
                  const errorClass = tableStyles.error;
                  if (params.row.message) {
                    return errorClass;
                  }
                  return '';
                }}
              />
            </Box>
            :
            <WarningMessage icon={<HistoryIcon />} message={`This token has not been used yet. Read the [API Documentation](${import.meta.env.VITE_API_DOCS}) for **implementation and code samples**.`} />
            }
          </>
        }
      </DialogContent>
      <>
        <DialogActions sx={{justifyContent: 'flex-end', pb: 3, px: 3}}>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </>
    </Dialog>

  )
}
